/**
 * Pet Actions Aggregation
 *
 * NOTE: Business logic (urgency scoring, nextDose calculation) is computed by the backend.
 * This module only combines and formats data for display.
 * For urgency values, use the backend's PetDashboardSummary.
 */
import type { Pet, PendingDose, RecentDose, Checkup, Vaccine, PetDashboardSummary } from '@rondela/shared'
import type { DashboardSortOption } from '../store'
import { parseLocalDateTime } from '../../../shared/lib/date-utils'

export interface Alert {
  type: 'medical_condition' | 'post_surgery'
  message: string
}

export interface PetWithActions {
  pet: Pet
  urgencyScore: number
  pendingDoses: PendingDose[]
  nextDose: PendingDose | null
  pendingDoseCount: number
  recentDoses: RecentDose[]
  upcomingAppointments: Checkup[]
  upcomingVaccines: Vaccine[]
  alerts: Alert[]
}

/**
 * Get alerts for a pet based on their medical conditions
 */
function getAlertsForPet(pet: Pet): Alert[] {
  const alerts: Alert[] = []

  if (pet.condicionesMedicas && pet.condicionesMedicas.trim().length > 0) {
    alerts.push({
      type: 'medical_condition',
      message: pet.condicionesMedicas,
    })
  }

  return alerts
}

/**
 * Get upcoming appointments (today and tomorrow) for a pet
 */
function getUpcomingAppointments(checkups: Checkup[], petId: string): Checkup[] {
  const now = new Date()
  const today = now.toISOString().split('T')[0]
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  return checkups.filter(
    (c) =>
      c.petId === petId &&
      c.proximaCita &&
      (c.proximaCita === today || c.proximaCita === tomorrow)
  )
}

/**
 * Get upcoming vaccines (next 7 days) for a pet
 */
function getUpcomingVaccines(vaccines: Vaccine[], petId: string): Vaccine[] {
  const now = new Date()
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

  return vaccines.filter((v) => {
    if (v.petId !== petId || !v.fechaProxima) return false
    const vaccineDate = new Date(v.fechaProxima)
    return vaccineDate >= now && vaccineDate <= weekFromNow
  })
}

/**
 * Find the next dose - prioritize upcoming doses, fall back to most recent overdue
 */
function findNextDose(doses: PendingDose[]): PendingDose | null {
  // Only consider pending doses
  const pendingOnly = doses.filter((d) => d.status === 'pending')
  if (pendingOnly.length === 0) return null

  const now = new Date()

  // Sort by scheduled time (earliest first)
  const sorted = [...pendingOnly].sort(
    (a, b) => parseLocalDateTime(a.scheduledFor).getTime() - parseLocalDateTime(b.scheduledFor).getTime()
  )

  // Find the first upcoming dose (in the future)
  const upcomingDose = sorted.find((d) => parseLocalDateTime(d.scheduledFor) >= now)
  if (upcomingDose) return upcomingDose

  // All doses are overdue - return the most recent one (last in sorted list)
  return sorted[sorted.length - 1] || null
}

/**
 * Aggregate pet data for detail views.
 *
 * NOTE: Urgency scoring comes from backend summaries.
 * This function only combines data for display.
 *
 * @param summaries - Backend dashboard summaries (provides urgency scores)
 */
export function aggregatePetActions(
  pets: Pet[],
  pendingDoses: PendingDose[],
  recentDoses: RecentDose[],
  checkups: Checkup[],
  vaccines: Vaccine[],
  sortOption: DashboardSortOption = 'urgency',
  summaries: PetDashboardSummary[] = []
): PetWithActions[] {
  const activePets = pets.filter((p) => p.status === 'active')

  // Create a map for quick lookup of backend summaries
  const summaryMap = new Map(summaries.map(s => [s.id, s]))

  const petsWithActions: PetWithActions[] = activePets.map((pet) => {
    const petPendingDoses = pendingDoses
      .filter((d) => d.petId === pet.id)
      .sort((a, b) => parseLocalDateTime(a.scheduledFor).getTime() - parseLocalDateTime(b.scheduledFor).getTime())

    const petRecentDoses = recentDoses.filter((d) => d.petId === pet.id)

    // Get urgency from backend summary (authoritative)
    const summary = summaryMap.get(pet.id)
    const urgencyScore = summary?.urgencyScore ?? 0

    // Get nextDose from backend summary if available, otherwise find locally
    let nextDose: PendingDose | null = null
    if (summary?.nextDose) {
      // Find the actual dose object that matches the backend's nextDose
      nextDose = petPendingDoses.find(d =>
        d.medicationId === summary.nextDose?.medicationId &&
        d.scheduledFor === summary.nextDose?.scheduledFor
      ) || null
    }
    // Fallback to local calculation only if backend doesn't provide
    if (!nextDose && petPendingDoses.length > 0) {
      nextDose = findNextDose(petPendingDoses)
    }

    return {
      pet,
      urgencyScore,
      pendingDoses: petPendingDoses,
      nextDose,
      pendingDoseCount: petPendingDoses.length,
      recentDoses: petRecentDoses,
      upcomingAppointments: getUpcomingAppointments(checkups, pet.id),
      upcomingVaccines: getUpcomingVaccines(vaccines, pet.id),
      alerts: getAlertsForPet(pet),
    }
  })

  // Sort based on selected option
  return sortPets(petsWithActions, sortOption)
}

/**
 * Sort pets based on the selected sort option
 */
export function sortPets(
  pets: PetWithActions[],
  sortOption: DashboardSortOption
): PetWithActions[] {
  const sorted = [...pets]

  switch (sortOption) {
    case 'urgency':
      sorted.sort((a, b) => b.urgencyScore - a.urgencyScore)
      break
    case 'medications':
      sorted.sort((a, b) => b.pendingDoseCount - a.pendingDoseCount)
      break
    case 'appointments':
      sorted.sort((a, b) => {
        const aDate = a.upcomingAppointments[0]?.proximaCita || '9999-12-31'
        const bDate = b.upcomingAppointments[0]?.proximaCita || '9999-12-31'
        return aDate.localeCompare(bDate)
      })
      break
    case 'alphabetical':
      sorted.sort((a, b) => a.pet.nombre.localeCompare(b.pet.nombre))
      break
  }

  return sorted
}
